import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Shield } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="text-sm text-stone-500 mt-1">Manage your account and preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="profile-card">
          <CardHeader>
            <CardTitle className="text-lg">Profile Information</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-emerald-900" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Name</p>
                <p className="font-medium">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                <Mail className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Shield className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Role</p>
                <Badge className="capitalize bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                  {user?.role}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="system-info-card">
          <CardHeader>
            <CardTitle className="text-lg">System Information</CardTitle>
            <CardDescription>CRM details and version</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-stone-500">Application</span>
              <span className="font-medium">Leisure Home Solutions CRM</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-stone-500">Version</span>
              <span className="font-medium">1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-stone-500">Environment</span>
              <Badge variant="outline">Production</Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2" data-testid="roles-info-card">
          <CardHeader>
            <CardTitle className="text-lg">User Roles</CardTitle>
            <CardDescription>Understanding role permissions in the CRM</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-stone-50 rounded-lg">
                <h4 className="font-medium text-emerald-900 mb-2">Admin</h4>
                <ul className="text-sm text-stone-600 space-y-1">
                  <li>• Full system access</li>
                  <li>• User management</li>
                  <li>• System settings</li>
                  <li>• All CRUD operations</li>
                </ul>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg">
                <h4 className="font-medium text-sky-600 mb-2">Manager</h4>
                <ul className="text-sm text-stone-600 space-y-1">
                  <li>• Manage cases & claims</li>
                  <li>• Approve reports & quotes</li>
                  <li>• Assign tasks</li>
                  <li>• View analytics</li>
                </ul>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg">
                <h4 className="font-medium text-amber-600 mb-2">Surveyor</h4>
                <ul className="text-sm text-stone-600 space-y-1">
                  <li>• Create & edit reports</li>
                  <li>• Update case status</li>
                  <li>• Manage own tasks</li>
                  <li>• View assigned cases</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
